'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { prolong } from '@/app/actions/reservation-modification'
import { formatActionError, handleStaleServerAction } from '@/root/modules/shared/EditReservation/utils'
import { useSessionCountdown } from './useSessionCountdown'

interface UseSessionProlongParams {
  expireAt: string
  getTranslation: (key: Key) => string
  isOpen: boolean
  locale: string
  onClose: () => void
  onEndEditing: () => void
  onError: (message: string, error?: unknown) => void
  /**
   * Where to go when the session expires while the modal is open and nobody pressed anything
   * (pfbnLaWP): called once per expiry, by the hook itself, unless a prolong or an end is under way.
   */
  onExpiredRedirect?: () => void
  onSessionExtended: (newExpireAt: string) => void
  reservationToken: string
}

export interface SessionProlong {
  endEditing: () => Promise<void>
  isBusy: boolean
  isEnding: boolean
  isExpired: boolean
  isProlonging: boolean
  prolongSession: () => Promise<void>
}

/**
 * Theme-agnostic session-prolong logic for the "session expiring" modal.
 * Owns the prolong/end-editing server actions and their async states, and
 * reuses useSessionCountdown for expiry detection (timer paused while closed). Once the
 * session has expired with the modal open, it forwards by itself (onExpiredRedirect).
 * The modal UI stays per-theme — this hook feeds it state + handlers.
 */
export const useSessionProlong = ({
  expireAt,
  getTranslation,
  isOpen,
  locale,
  onClose,
  onEndEditing,
  onError,
  onExpiredRedirect,
  onSessionExtended,
  reservationToken,
}: UseSessionProlongParams): SessionProlong => {
  const [isProlonging, setIsProlonging] = useState(false)
  const [isEnding, setIsEnding] = useState(false)
  const { isExpired } = useSessionCountdown({ expireAt, enabled: isOpen })
  const isBusy = isProlonging || isEnding
  const redirected = useRef(false)

  // pfbnLaWP: the person who walked away is the one who pressed nothing - the expired modal must not
  // wait for them. A prolong or an end already under way finishes first: its own outcome decides.
  useEffect(() => {
    if (!isOpen || !isExpired) {
      redirected.current = false
      return
    }
    if (isBusy || redirected.current) return
    redirected.current = true
    onExpiredRedirect?.()
  }, [isOpen, isExpired, isBusy, onExpiredRedirect])

  const prolongSession = useCallback(async () => {
    setIsProlonging(true)
    let result: Awaited<ReturnType<typeof prolong>>

    try {
      result = await prolong(reservationToken, locale)
    } catch (err) {
      setIsProlonging(false)

      if (handleStaleServerAction({ error: err, getTranslation, onError })) {
        onClose()
        return
      }

      throw err
    }

    setIsProlonging(false)

    if (!result.success) {
      onClose()
      const errorMessage = formatActionError(
        getTranslation('editSessionProlongFailed'),
        result.error.code,
        getTranslation
      )
      onError(errorMessage, { status: result.error.status })
      return
    }

    const newExpireAt = result.data.modifiedReservation?.expireAt
    if (newExpireAt) {
      onSessionExtended(newExpireAt)
    }
    onClose()
  }, [reservationToken, locale, onClose, onSessionExtended, onError, getTranslation])

  const endEditing = useCallback(async () => {
    setIsEnding(true)
    await onEndEditing()
  }, [onEndEditing])

  return { endEditing, isBusy, isEnding, isExpired, isProlonging, prolongSession }
}
